import {EstimateTreeOperationDto} from "../dtos/estimate-tree-operation.dto";

/**
 * Time-first stepper math (AC-267 B1), mirroring the Android app's
 * WorkItemsController / PriceDistribution so both platforms edit operations
 * the same way: steppers move WALL-CLOCK time in 15-minute taps, stored
 * minutes are TOTAL LABOR (wall × crew), and prices are derived server-side
 * from the minutes.
 */
export const STEP_WALL_MINUTES = 15;

/**
 * The labor minutes the steppers act on: the override wins, then the stored
 * minutes; a legacy cost-only row falls back to the server's own inverse
 * derivation (round(cost / rate × 60)) so stepping adjusts the existing
 * price instead of restarting from zero.
 */
export function effectiveLaborMinutes(
  operation: EstimateTreeOperationDto,
  billedHourlyLaborRate: number,
): number {
  if (operation.estimatedMinutesOverride != null) return operation.estimatedMinutesOverride;
  if (operation.estimatedMinutes != null) return operation.estimatedMinutes;
  if (operation.estimatedCost != null && billedHourlyLaborRate > 0) {
    return Math.round(operation.estimatedCost / billedHourlyLaborRate * 60);
  }
  return 0;
}

/** Stored minutes are total labor; on screen we show wall-clock time. */
export function wallClockMinutes(laborMinutes: number, crewSize: number): number {
  return crewSize <= 1 ? laborMinutes : Math.round(laborMinutes / crewSize);
}

/**
 * SQ-520: measured time (crew worked time, an operation's actual time, the
 * AI's suggested time) is stored and exchanged in seconds and shown as whole
 * minutes. Nearest minute, half up — 89 s is 1, 90 s is 2 — the same rule as
 * the server, the database and Android.
 */
export function secondsToMinutes(seconds: number): number {
  return Math.round(Math.max(0, seconds) / 60);
}

export function minutesToSeconds(minutes: number): number {
  return minutes * 60;
}

export function formatMinutes(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

/**
 * Proportional distribution for the tree- and estimate-level total steppers
 * (Android PriceDistribution port): scales currentValues so they sum to
 * roughly newTotal while keeping their proportions. When the current total is
 * 0 the new total is split evenly, with the remainder going to the first
 * entries so the sum stays exact.
 */
export function distributeMinutes(currentValues: number[], newTotal: number): number[] {
  if (currentValues.length === 0) return [];
  const currentTotal = currentValues.reduce((acc, value) => acc + value, 0);
  if (currentTotal === 0) {
    const share = Math.floor(newTotal / currentValues.length);
    const remainder = newTotal - share * currentValues.length;
    return currentValues.map((_, index) => (index < remainder ? share + 1 : share));
  }
  const ratio = newTotal / currentTotal;
  return currentValues.map(value => Math.round(value * ratio));
}
